"use client";

import { cn } from "@/lib/utils";
import type { ImageDetails } from "@/types";
import { motion } from "framer-motion";
import Image from "next/image";
import { Container } from "./Container";

export type TestimonialSectionProps = {
	id?: string;
	image: ImageDetails;
	quote: string;
	author: {
		name?: string;
		title?: string;
		company?: string;
	};
	className?: string;
};

export const TestimonialSection = ({
	image,
	quote,
	author,
	id,
	className,
}: TestimonialSectionProps) => {
	return (
		<section id={id} className={cn(className)}>
			<div className="mt-40 mb-24 bg-gradient-to-b from-zinc-950 to-zinc-800 pt-72 pb-24 lg:pt-36">
				<Container>
					<div className="grid grid-cols-1 lg:grid-cols-[384px_1fr_1fr]">
						<div className="-mt-96 lg:-mt-52">
							<motion.div
								initial={{ opacity: 0, y: 20 }}
								whileInView={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.7, ease: "easeOut" }}
								viewport={{ once: true }}
								className="-m-2 rounded-4xl bg-white/15 shadow-[inset_0_0_2px_1px_#ffffff4d] ring-1 ring-black/5 max-lg:mx-auto max-lg:max-w-xs"
							>
								<div className="rounded-4xl p-2 shadow-black/5 shadow-md">
									<div className="-outline-offset-1 overflow-hidden rounded-3xl shadow-2xl outline-1 outline-black/10">
										<Image
											alt={image.alt}
											src={image.src}
											width={image.width || 1200}
											height={image.height || 1200}
											className="aspect-3/4 w-full object-cover"
										/>
									</div>
								</div>
							</motion.div>
						</div>
						<div className="flex max-lg:mt-16 lg:col-span-2 lg:px-16">
							<figure className="mx-auto flex max-w-xl flex-col gap-16 max-lg:text-center">
								<motion.blockquote
									initial={{ opacity: 0, y: 30 }}
									whileInView={{ opacity: 1, y: 0 }}
									transition={{ duration: 0.7, delay: 0.2 }}
									viewport={{ once: true }}
								>
									<p className="relative text-white text-xl tracking-tight lg:text-2xl">
										"{quote}"
									</p>
								</motion.blockquote>
								<motion.figcaption
									className="mt-auto"
									initial={{ opacity: 0 }}
									whileInView={{ opacity: 1 }}
									transition={{ duration: 0.5, delay: 0.4 }}
									viewport={{ once: true }}
								>
									<p className="font-medium text-sm/6 text-white">
										{author?.name}
									</p>
									<p className="font-medium text-sm/6">
										<span className="bg-linear-115 from-28% from-primary via-70% via-secondary to-primary bg-clip-text text-transparent">
											{author?.title} {author?.company}
										</span>
									</p>
								</motion.figcaption>
							</figure>
						</div>
					</div>
				</Container>
			</div>
		</section>
	);
};
